import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import type { CrmDataProvider } from "../providers/types";
import type { PublicQuote } from "../types";
import { PlanSchedule, formatMxn, tryCalculateSchedule } from "./PlanSchedule";
import { QuoteSheetOne, QuoteSheetTwo } from "./PublicQuoteSheets";
import { buildSummaryMessage, whatsappUrl } from "./quoteWhatsapp";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(APP_LOCALE, NUMERIC_DATE_FORMAT);

/**
 * Almikia Epic EQ (TASK-019): the client-facing quote for a share token. Pure
 * view over one anonymous lookup (`getPublicQuote`); the token is only used as
 * the lookup argument and is never logged, rendered or put in an error.
 * Invalid, expired, revoked and cancelled links are indistinguishable.
 */
export const PublicQuoteView = ({ token }: { token: string }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const {
    data: quote,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["public-quote", token],
    queryFn: () => dataProvider.getPublicQuote(token),
    retry: false,
  });

  return (
    <main className="min-h-screen bg-background px-4 py-8 text-base text-foreground print:min-h-0 print:bg-white print:p-0">
      {/* noindex + no-referrer: React 19 hoists these into <head>. */}
      <meta name="robots" content="noindex, nofollow" />
      <meta name="referrer" content="no-referrer" />
      <div
        className={`mx-auto flex flex-col gap-4 ${quote?.detail ? "max-w-4xl print:max-w-none" : "max-w-xl"}`}
      >
        <p
          className={`text-sm font-semibold uppercase tracking-wide ${quote?.detail ? "print:hidden" : ""}`}
        >
          {translate("crm.quoter.public.brand")}
        </p>
        {isPending ? (
          <Card role="status" aria-busy="true">
            <CardContent className="space-y-4">
              <span className="sr-only">{translate("crm.common.loading")}</span>
              <Skeleton className="h-8 w-3/5" />
              <Skeleton className="h-6 w-4/5" />
              <Skeleton className="h-40 w-full" />
            </CardContent>
          </Card>
        ) : isError ? (
          <Card>
            <CardContent className="space-y-4" role="alert">
              <p>{translate("crm.quoter.public.load_error")}</p>
              <Button
                variant="outline"
                className="min-h-11"
                onClick={() => refetch()}
              >
                {translate("crm.common.retry")}
              </Button>
            </CardContent>
          </Card>
        ) : quote?.detail ? (
          <QuoteSheets quote={quote} />
        ) : quote ? (
          <QuoteDetails quote={quote} />
        ) : (
          <Card>
            <CardContent>
              <p role="alert">{translate("crm.quoter.public.unavailable")}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
};

/**
 * Almikia Epic EQ2 (TASK-101): the two printable sheets plus the actions that
 * never print (save as PDF through the browser, share the summary).
 */
const QuoteSheets = ({ quote }: { quote: PublicQuote }) => {
  const translate = useTranslate();
  return (
    <>
      <QuoteSheetOne quote={quote} />
      <QuoteSheetTwo quote={quote} />
      <div className="flex flex-col gap-2 sm:flex-row print:hidden">
        <Button
          variant="gradient"
          className="min-h-11 flex-1"
          onClick={() => window.print()}
        >
          {translate("crm.quoter.public.save_pdf")}
        </Button>
        <Button asChild variant="outline" className="min-h-11 flex-1">
          <a
            href={whatsappUrl(null, buildSummaryMessage(quote, translate))}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate("crm.quoter.public.share_whatsapp")}
          </a>
        </Button>
      </div>
    </>
  );
};

const QuoteDetails = ({ quote }: { quote: PublicQuote }) => {
  const translate = useTranslate();
  const rows = tryCalculateSchedule(quote.unit_price, quote.plan);
  return (
    <Card className="print:shadow-none">
      <CardContent className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">
            {translate("crm.quoter.public.greeting", {
              name: quote.client_first_name,
            })}
          </h1>
          <p>{translate("crm.quoter.public.intro")}</p>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
          <dt className="text-muted-foreground">
            {translate("crm.quoter.project")}
          </dt>
          <dd className="font-medium">{quote.project_name}</dd>
          <dt className="text-muted-foreground">
            {translate("crm.quoter.unit")}
          </dt>
          <dd className="font-medium">
            {quote.unit_identifier} ·{" "}
            {translate(`crm.quoter.unit_types.${quote.unit_type}`)}
          </dd>
          <dt className="text-muted-foreground">
            {translate("crm.quoter.public.price")}
          </dt>
          <dd className="text-lg font-bold tabular-nums">
            {formatMxn(quote.unit_price)}
          </dd>
        </dl>
        <section className="space-y-2 print:break-inside-avoid">
          <h2 className="text-lg font-semibold">
            {translate("crm.quoter.schedule.title")}
          </h2>
          {rows ? (
            <PlanSchedule rows={rows} showPercentage={false} large />
          ) : (
            <p role="alert">{translate("crm.quoter.public.load_error")}</p>
          )}
        </section>
        <p className="text-muted-foreground">
          {translate("crm.quoter.public.valid_until", {
            date: formatDate(quote.valid_until),
          })}
        </p>
        <Button
          variant="gradient"
          className="min-h-11 w-full print:hidden"
          onClick={() => window.print()}
        >
          {translate("crm.quoter.public.save_pdf")}
        </Button>
      </CardContent>
    </Card>
  );
};

/** Public route element (`/cotizacion/:token`), registered in the `noLayout` routes. */
export const PublicQuotePage = () => {
  const { token = "" } = useParams<{ token: string }>();
  return <PublicQuoteView token={token} />;
};

PublicQuotePage.path = "/cotizacion/:token";
